import { useCallback, useEffect, useRef, useState } from 'react';

export type TUseAnimationFrameArgs = {
    callback: (delta: number) => void,
    onEndCallback: () => void,
    duration: number,
    timingFunction?: (time: number) => number,
}

export type TUseAnimationFrameState = {
    state: 'waiting' | 'process' | 'start',
    delta: number,
}

/**
 * @link https://javascript.info/js-animation
 */
const useCompletionAnimation = (
    {
        callback,
        onEndCallback,
        timingFunction = (time: number) => time,
        duration,
    }: TUseAnimationFrameArgs,
) => {
    const [state, setState] = useState<TUseAnimationFrameState>({
        state: 'waiting',
        delta: 0,
    });
    const start = useCallback(() => {
        startTimeRef.current = performance.now();
        setState(prev => ({ ...prev, state: 'start' }));
    }, []);
    const rafRef = useRef<number>(0);
    const startTimeRef = useRef<number>(0);

    const loop: FrameRequestCallback = (time: number) => {
        let fraction = (time - startTimeRef.current) / duration;
        if (fraction > 1) {
            fraction = 1;
        }

        const delta = timingFunction(fraction);

        setState(prev => ({ ...prev, delta }));
        callback(delta);

        if (fraction < 1) {
            rafRef.current = requestAnimationFrame(loop);
        } else {
            startTimeRef.current = 0;
            setState(prev => ({ ...prev, state: 'waiting' }));
        }
    };

    useEffect(() => {
        if (state.state === 'process') {
            rafRef.current = requestAnimationFrame(loop);
            return () => cancelAnimationFrame(rafRef.current);
        }

        if (state.state === 'start') {
            setState(prev => ({ ...prev, state: 'process' }));
        }

        if (state.state === 'waiting') {
            onEndCallback();
        }
    }, [state]);

    return {
        start,
        ...state,
    };
};

export default useCompletionAnimation;
